import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowLeft, ExternalLink, Loader2, ShieldAlert, AppWindow, RefreshCw, LogOut, ScrollText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatusPageEmbed } from "@/components/StatusPageEmbed";
import { StatusPagePanel } from "@/components/StatusPagePanel";
import AuditLogPage from "@/pages/AuditLogPage";

function DynIcon({ name, className }) {
  const C = Icons[name] || AppWindow;
  return <C className={className} />;
}

// Iter 103 — Human-readable copy for SSO failure modes. User-facing
// strings intentionally use the app's own display name (passed in as
// `appName`) instead of the vendor name — staff-facing runbooks in
// Settings → Integrations still reference the real vendor.
function ssoErrorCopy(raw, appName) {
  const s = String(raw || "");
  const name = appName || "this app";
  if (s.includes("privileges")) {
    return `Your account has administrator privileges in ${name} — the vendor blocks admins from auto-login for security. Open in a new tab and sign in with your ${name} credentials.`;
  }
  if (s.includes("branch URLs")) {
    return `Your ${name} account is scoped to a branch and this launch context is missing the branch URL. This should be fixed automatically on the next launch — try Reload below.`;
  }
  if (s.includes("not_configured")) {
    return `${name} SSO is not configured yet. Contact your platform admin.`;
  }
  if (s.includes("no_username")) {
    return `Your ${name} username hasn't been set yet. Ask your platform admin to fill it in, or open ${name} in a new tab to sign in manually.`;
  }
  if (s.includes("rate_limited")) {
    return `${name} is rate-limiting SSO right now. Please try again in a minute, or open in a new tab.`;
  }
  if (s.includes("timeout")) {
    return `${name} SSO took too long to respond. Try Reload below or open in a new tab.`;
  }
  if (s.includes("sync_missing_user")) {
    return `Your ${name} account is still being provisioned. Wait a few seconds and click Reload — if it persists after two retries, contact your platform admin.`;
  }
  return `Auto-login is temporarily unavailable (${s.slice(0, 120) || "unknown"}). Open in a new tab and sign in manually.`;
}

/** Iter 119m — Friendly "you're not authorized" card, replacing both the raw
 *  403 from /launch and the vendor's own 401 page inside the iframe. */
function UnauthorizedCard({ appName, onBack, backLabel, adminHint }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-10 text-center" data-testid="app-viewer-unauthorized">
      <ShieldAlert className="mx-auto h-10 w-10 text-amber-600" />
      <div className="mt-3 font-heading text-lg font-semibold text-amber-900" data-testid="app-viewer-unauthorized-title">
        You&apos;re not authorized to use {appName || "this app"}
      </div>
      <p className="mx-auto mt-2 max-w-lg text-sm text-amber-800" data-testid="app-viewer-unauthorized-message">
        Your account doesn&apos;t have access to {appName || "this app"} yet. Please contact your
        {" "}<span className="font-semibold">Partner Success Manager</span> to request authorization.
      </p>
      {adminHint && (
        <p className="mx-auto mt-3 max-w-lg rounded-md bg-amber-100/70 px-3 py-2 text-xs text-amber-900" data-testid="app-viewer-unauthorized-hint">
          <span className="font-semibold">Admin hint:</span> {adminHint}
        </p>
      )}
      {onBack && (
        <Button variant="outline" className="mt-5" onClick={onBack} data-testid="app-viewer-unauthorized-back">
          <ArrowLeft className="h-4 w-4" /> Back to {backLabel || "Dashboard"}
        </Button>
      )}
    </div>
  );
}

export default function AppViewer() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const backTo = "/dashboard";
  const backLabel = "Dashboard";
  const [app, setApp] = useState(null);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [ssoRejected, setSsoRejected] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  // Iter 215 — true while the hidden peer-logout frame is still running;
  // the visible iframe is held back until it settles (onLoad + boot grace, or 7s).
  const [purging, setPurging] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const requested = useRef(false);
  const logoutIframeRef = useRef(null);
  const iframeRef = useRef(null);
  // Iter 108k — Per-launch state used to report browser-side observations
  // to the OAuth debug console. We capture the load start timestamp on
  // src assignment, count onLoad events (redirects fire multiple), and
  // ride window.onerror to catch cross-origin "X-Frame-Options" console
  // errors that would otherwise be invisible to Conduit.
  const traceRef = useRef({ start_ms: 0, load_count: 0, xfo: false, err_text: "" });

  // Iter 101 — Fetch launch metadata. Re-runs whenever the Conduit user
  // switches or the operator clicks Reload so the iframe never inherits
  // the previous user's SSO context.
  useEffect(() => {
    requested.current = false;
    setApp(null);
    setError("");
    setForbidden(false);
    setSsoRejected(false);
    setIframeLoading(true);
    (async () => {
      if (requested.current) return;
      requested.current = true;
      try {
        const { data } = await api.post(`/apps/${appId}/launch`);
        setApp(data);
        // Iter 119s — Quoter answers a bad login with its own "Error 401:
        // Unauthorized / Invalid login link" page, which we can't read from a
        // cross-origin iframe. Probe the SSO link server-side and swap in our
        // own message when the vendor rejects the sign-in.
        if (data.sso_ok && data.embed_allowed) {
          api.post(`/apps/${appId}/sso-probe`)
            .then((r) => { if (r.data?.checked && r.data?.ok === false) setSsoRejected(true); })
            .catch(() => {});
        }
        // Iter 108s — Auto-open new tab logic.
        //   • If the app has `newtab_only=true` AND `auto_open_new_tab=true`,
        //     open the peer URL immediately (the amber instruction card
        //     will still render behind, so the user sees context if the
        //     pop-up is blocked or they navigate back).
        //   • Otherwise fall back to the Iter 102 rule: only auto-open
        //     when `embed_allowed=false` AND `auto_open_new_tab=true`.
        // Both branches use the same fallback URL priority as the
        // "Open in new tab" button: embed_url first, then plain url.
        const auto_target = data.embed_url || data.url;
        if (data.newtab_only === true && data.auto_open_new_tab === true && auto_target) {
          window.open(auto_target, "_blank");
        } else if (!data.newtab_only && !data.embed_allowed && data.auto_open_new_tab === true) {
          window.open(data.url, "_blank");
        }
      } catch (e) {
        setForbidden(e.response?.status === 403);
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
  }, [appId, reloadKey, user?.user_id]);

  // Iter 215 — Peer session purge, sequenced. When the linked client has a
  // logout URL, load it in the hidden frame first and only render the
  // visible iframe once it settles, so the peer cannot answer the start
  // URL with a session that belongs to a different Conduit identity
  // (the operator's own while masquerading, or a previous user's).
  // Iter 252 — SPA peers (Structured AI's /session-expired) only run their
  // logout routine after their JS boots, i.e. well after the frame's onLoad,
  // and both frames share the peer's sessionStorage. So: wait onLoad + a
  // boot grace before releasing the visible iframe, then park the hidden
  // frame on about:blank so a peer auto-redirect can't start a second
  // handshake that races the visible one.
  const PURGE_BOOT_GRACE_MS = 2500;
  const PURGE_HARD_CAP_MS = 7000;
  const purgeTimers = useRef([]);
  const finishPurge = () => {
    purgeTimers.current.forEach(clearTimeout);
    purgeTimers.current = [];
    setPurging(false);
    const frame = logoutIframeRef.current;
    if (frame) { try { frame.src = "about:blank"; } catch { /* detached */ } }
  };
  const onPurgeFrameLoad = () => {
    const frame = logoutIframeRef.current;
    if (!frame || !frame.src || frame.src === "about:blank") return;
    purgeTimers.current.push(setTimeout(finishPurge, PURGE_BOOT_GRACE_MS));
  };
  useEffect(() => {
    if (!app?.sso_logout_url) { setPurging(false); return undefined; }
    const frame = logoutIframeRef.current;
    const purgeUrl = () => app.sso_logout_url + (app.sso_logout_url.includes("?") ? "&" : "?") + `t=${Date.now()}`;
    setPurging(true);
    if (frame) frame.src = purgeUrl();
    purgeTimers.current.push(setTimeout(finishPurge, PURGE_HARD_CAP_MS));
    return () => {
      purgeTimers.current.forEach(clearTimeout);
      purgeTimers.current = [];
      if (frame) {
        try { frame.src = purgeUrl(); }
        catch { /* ignore — component unmounting */ }
      }
    };
  }, [app?.sso_logout_url, user?.user_id, reloadKey]);

  useEffect(() => {
    if (!app || !app.embed_allowed) return;
    setIframeLoading(true);
  }, [app, reloadKey]);

  // Iter 108k — Browser-side OAuth trace reporter. Conduit is not in the
  // browser↔peer conversation after `/userinfo`, so we forward what we
  // *can* observe from here into system_logs. IMPORTANT limitation:
  // browsers block the parent from reading cross-origin iframe URLs,
  // contentDocument, or console CSP/X-Frame-Options violations. The
  // observable signals we capture are:
  //   1. onLoad timing (elapsed since src assignment) + first-load ms
  //   2. postMessage from the peer (TalentLMS bundle emits some)
  //   3. window.onerror (rarely fires for XFO blocks in modern browsers)
  //   4. onLoad count — always 1 for cross-origin regardless of block
  useEffect(() => {
    if (!app || !app.embed_allowed || !app.oauth_client_id) return undefined;
    traceRef.current = {
      start_ms: Date.now(),
      load_count: 0,
      xfo: false,
      err_text: "",
      peer_messages: [],
      first_load_ms: 0,
    };

    const onWindowError = (ev) => {
      const msg = String(ev?.message || ev?.error?.message || "");
      if (/x-frame-options|refused to display|frame-ancestors/i.test(msg)) {
        traceRef.current.xfo = true;
        traceRef.current.err_text = msg.slice(0, 500);
      }
    };
    window.addEventListener("error", onWindowError, true);

    // Any postMessage from the peer — logged as-is (redacted at trace).
    const onMessage = (ev) => {
      try {
        const src = ev.origin || "";
        if (!/talentlms|iplogin|unitedcloud/i.test(src)) return;
        const data = typeof ev.data === "string"
          ? ev.data
          : JSON.stringify(ev.data);
        traceRef.current.peer_messages.push(`${src} → ${data.slice(0, 160)}`);
      } catch { /* ignore */ }
    };
    window.addEventListener("message", onMessage);

    let reportTimer = null;
    const schedule = () => {
      if (reportTimer) clearTimeout(reportTimer);
      reportTimer = setTimeout(sendReport, 2500);
    };
    // Iter 115y — 8s was too tight for slower peers (Training reported a false
    // "peer hung"); the diagnostic cap is now 15s.
    const capTimer = setTimeout(sendReport, 15000);

    let sent = false;
    function sendReport() {
      if (sent) return;
      sent = true;
      const t = traceRef.current;
      const elapsed = Math.max(0, Date.now() - t.start_ms);
      const messages = (t.peer_messages || []).slice(0, 6);
      let verdict;
      if (t.xfo) {
        verdict = "X-Frame-Options / frame-ancestors block";
      } else if (t.load_count === 0 && elapsed >= 14500) {
        verdict = "no iframe onLoad fired within 15s (peer hung or blocked pre-load)";
      } else {
        verdict = `iframe onLoad fired ×${t.load_count} · first at ${t.first_load_ms}ms`;
      }
      api.post("/oauth/trace/browser", {
        client_id:    app.oauth_client_id || "",
        app_id:       app.app_id || "",
        app_name:     app.name || "",
        elapsed_ms:   elapsed,
        final_url:    "",  // cross-origin: unreadable by design
        error_text:   t.err_text || (messages.length
                        ? `peer messages: ${messages.join(" || ")}`
                        : ""),
        xfo_blocked:  t.xfo,
        load_count:   t.load_count,
        context_hint: (user?.masqueraded_by ? "masqueraded · " : "direct · ") + verdict,
      }).catch(() => { /* trace-only, never surface */ });
    }
    traceRef.current._schedule = schedule;
    return () => {
      window.removeEventListener("error", onWindowError, true);
      window.removeEventListener("message", onMessage);
      if (reportTimer) clearTimeout(reportTimer);
      clearTimeout(capTimer);
      if (!sent) sendReport();
    };
  }, [app?.app_id, app?.oauth_client_id, reloadKey, user?.user_id]);

  if (error) {
    if (forbidden) {
      return <UnauthorizedCard onBack={() => navigate(backTo)} backLabel={backLabel} />;
    }
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-10 text-center" data-testid="app-viewer-error">
        <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
        <p className="mt-3 text-sm text-slate-600">{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(backTo)}><ArrowLeft className="h-4 w-4" /> Back to {backLabel}</Button>
      </div>
    );
  }

  if (!app) {
    return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  // Iter 102 — Training-vendor SSO failed path. We intentionally do NOT
  // Iter 102 — Generalized SSO-failure state. When any backend-minted
  // SSO provider (TalentLMS, Quoter, …) fails, refuse to render the
  // iframe with a fallback URL: doing so would surface whichever user's
  // session cookie is already in the browser (the cross-user leak the
  // TalentLMS rework fixed). Force an explicit error state with a
  // clear "Open in new tab" affordance instead.
  const ssoFailed = !!app.sso_provider && app.sso_ok === false;
  // Fallback URL to hand the user for a manual login. For TalentLMS we
  // use `app.url`. Quoter surfaces its own `sso_fallback_url` so users
  // land on the socketapp shell rather than the SSO endpoint.
  const fallbackUrl = app.sso_fallback_url || app.url;
  // Iter 108s — For new-tab-only apps we want to send the user to the
  // SSO init URL (embed_url) when it's minted so they still get signed
  // in via OIDC. Falls back to the plain URL when no OAuth linkage
  // exists yet on the app.
  const newtabTarget = app.embed_url || app.url;

  return (
    <div data-testid="app-viewer">
      {/* Iter 215 — Hidden iframe that purges the peer's session before
          the visible iframe renders. Kept mounted for the app's whole
          lifetime so the cleanup also fires on unmount. */}
      {app.sso_logout_url && (
        <iframe
          ref={logoutIframeRef}
          title={`${app.name} session cleanup`}
          data-testid="app-viewer-peer-logout"
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: "none" }}
          onLoad={onPurgeFrameLoad}
          sandbox="allow-same-origin allow-scripts allow-forms"
        />
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" data-testid="app-viewer-back" onClick={() => navigate(backTo)}><ArrowLeft className="h-4 w-4" /> {backLabel}</Button>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#eef2fb] text-primary"><DynIcon name={app.icon} className="h-5 w-5" /></span>
          <div>
            <div className="font-heading text-base font-semibold text-slate-900">{app.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Iter 119x — The Phone Portal Audit Log also lives as a section
              inside the Conduit Activity Log. This deep link jumps there
              with that section already expanded. */}
          {app.slug === "audit-log" && (
            <Button variant="outline" size="sm" data-testid="app-viewer-open-in-activity"
              onClick={() => navigate("/activity?section=audit")}>
              <ScrollText className="h-4 w-4" /> Open in Activity Log
            </Button>
          )}
          <Button variant="outline" size="sm" data-testid="app-viewer-reload" onClick={() => setReloadKey((k) => k + 1)}><RefreshCw className="h-4 w-4" /> Reload</Button>
          <Button variant="outline" size="sm" data-testid="app-viewer-newtab" disabled={!app.url} onClick={() => app.url && window.open(app.url, "_blank")}><ExternalLink className="h-4 w-4" /> Open in new tab</Button>
        </div>
      </div>

      {/* Iter 135 — Account-linking notice. TalentLMS matches an SSO login
          by username, so Conduit aligns the LMS username with the user's
          email before launch. When that can't be done (the LMS refuses API
          edits on its own admins) the vendor would otherwise show a bare
          "Someone is already using this email address" page — say so here
          instead. */}
      {app.slug === "status-page" ? (
        // Iter 110d — Native status page renderer. incident.io's hosted
        // status page ships X-Frame-Options: SAMEORIGIN and CSP
        // frame-ancestors 'self' so an iframe embed can never load in
        // Conduit. Iter 110ag — Replaced the widget-only embed with
        // `StatusPagePanel`, which mirrors the top-bar Current Status
        // + Uptime Stats widgets in a full-page layout. A
        // super-admin-only toggle inside the panel still lets us
        // flip to the incident.io widget once 90 days of history
        // has accumulated.
        <StatusPagePanel appUrl={app.url} />
      ) : app.slug === "audit-log" ? (
        // Iter 110e — Native Audit Log app. Queries the selected MySQL
        // DB Connection through the backend proxy; enforces
        // audit_log.view / audit_log.export permissions and (for
        // partners) territory-scoped WHERE clause server-side.
        <AuditLogPage />
      ) : app.newtab_only ? (        // Iter 108s — New-tab-only state. This app opts out of iframe
        // rendering entirely, even when `embed_allowed=true`, because
        // the vendor's post-login page ships `X-Frame-Options` /
        // frame-ancestors that would blank the iframe. Users get a
        // light-orange instruction card with a single CTA linked to
        // the SSO init URL. When `auto_open_new_tab` is ALSO on, the
        // card renders first (so the user sees why the tab opened)
        // and then the new tab fires — handled in the fetch effect.
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-10 text-center" data-testid="app-viewer-newtab-only">
          <ExternalLink className="mx-auto h-10 w-10 text-amber-600" />
          <div className="mt-3 font-heading text-lg font-semibold text-amber-900">{app.name} opens in a new tab</div>
          {app.auto_open_new_tab && (
            <p className="mx-auto mt-3 max-w-lg text-xs text-amber-700/80">
              A new tab should have just opened automatically. If your browser blocked the pop-up, use the button below.
            </p>
          )}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <Button className="bg-amber-600 text-white hover:bg-amber-700" disabled={!newtabTarget || purging} onClick={() => newtabTarget && window.open(newtabTarget, "_blank")} data-testid="newtab-only-open">
              {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} {purging ? "Clearing previous session…" : `Open ${app.name} in a new tab`}
            </Button>
          </div>
        </div>
      ) : ssoRejected ? (
        <UnauthorizedCard appName={app.name} />
      ) : ssoFailed ? (
        // Iter 102 — Explicit failure state for any SSO provider.
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-10 text-center" data-testid="app-viewer-sso-blocked">
          <ShieldAlert className="mx-auto h-10 w-10 text-amber-600" />
          <div className="mt-3 font-heading text-lg font-semibold text-amber-900">Auto sign-in blocked</div>
          <p className="mx-auto mt-2 max-w-lg text-sm text-amber-800">
            {ssoErrorCopy(app.sso_error, app.name)}
          </p>
          <p className="mx-auto mt-3 max-w-lg text-xs text-amber-700/80">
            For your security, {app.name} will <span className="font-semibold">not</span> be displayed inline until sign-in succeeds — this prevents another user&apos;s session from being shown accidentally.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <Button className="bg-amber-600 text-white hover:bg-amber-700" onClick={() => window.open(fallbackUrl, "_blank")} data-testid="sso-blocked-newtab">
              <ExternalLink className="h-4 w-4" /> Open {app.name} in a new tab
            </Button>
            <Button variant="outline" onClick={() => setReloadKey((k) => k + 1)} data-testid="sso-blocked-retry">
              <RefreshCw className="h-4 w-4" /> Retry auto sign-in
            </Button>
            {app.sso_logout_url && (
              <Button variant="outline" onClick={() => window.open(app.sso_logout_url, "_blank")} data-testid="sso-blocked-logout">
                <LogOut className="h-4 w-4" /> Sign out of {app.name}
              </Button>
            )}
          </div>
        </div>
      ) : (!app.url && !app.embed_url) ? (
        // Iter 108o — Config-incomplete state for apps that have been
        // provisioned in the DB but not yet given a target URL (e.g.
        // the new "Marketing" app before the operator wires its
        // OIDC init URL). The "Open in new tab" failover would be a
        // dead link, so we surface a friendly "not configured yet"
        // card instead of a stuck iframe.
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-10 text-center" data-testid="app-viewer-not-configured">
          <ExternalLink className="mx-auto h-10 w-10 text-slate-400" />
          <div className="mt-3 font-heading text-lg font-semibold text-slate-800">{app.name} isn&apos;t configured yet</div>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
            An administrator needs to set the target URL and OAuth2 client for {app.name} before it can be launched.
          </p>
        </div>
      ) : !app.embed_allowed ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center" data-testid="app-viewer-external">
          <ExternalLink className="mx-auto h-10 w-10 text-primary" />
          <div className="mt-3 font-heading text-lg font-semibold text-slate-800">{app.name} opens in a new tab</div>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">This app is configured for external launch only.</p>
          <Button className="mt-4 bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => window.open(app.url, "_blank")}><ExternalLink className="h-4 w-4" /> Launch app</Button>
        </div>
      ) : (
        <div className="relative w-full overflow-hidden rounded-lg border border-slate-200 bg-white" style={{ height: "calc(100vh - 220px)", minHeight: 480 }} data-testid="app-iframe-container">
          {(iframeLoading || purging) && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80" data-testid="app-iframe-loading">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          {!purging && (
          <iframe
            // Iter 101 — key ties the iframe to (app, reload, Conduit user)
            // so React tears down + remounts the frame on any user switch
            // or reload. Prevents any state carryover from a prior user.
            key={`${app.app_id}-${reloadKey}-${user?.user_id || "anon"}`}
            ref={iframeRef}
            title={app.name}
            src={app.embed_url || app.url}
            className="h-full w-full border-0"
            onLoad={() => {
              setIframeLoading(false);
              // Iter 108k — Count every onLoad (some browsers fire only
              // once at the end of the redirect chain, others fire per
              // committed navigation) and reschedule the trace report
              // so it waits until the chain settles.
              const t = traceRef.current || {};
              t.load_count = (t.load_count || 0) + 1;
              if (!t.first_load_ms) {
                t.first_load_ms = Math.max(0, Date.now() - (t.start_ms || Date.now()));
              }
              if (t._schedule) t._schedule();
            }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-modals allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer-when-downgrade"
          />
          )}
        </div>
      )}
    </div>
  );
}
